import api from 'helpers/api';
import { useAngularService } from 'helpers/get_angular_service';
import notification from 'helpers/notification';
import sources from 'helpers/sources';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../components/button';
import ErrorMessage from '../../components/error_message';
import Icon from '../../components/icon';
import Input from '../../components/input';

interface Category {
  id?: number;
  name?: string;
  errors?: any;
}

const ManageCreditVoucherCategories = ({ modalInstance }) => {
  const $stateParams = useAngularService('$stateParams');
  const [categories, setCategories] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [editCategory, setEditCategory] = useState<Category>({});
  const [newCategory, setNewCategory] = useState<Category>({ name: '' });
  const baseUrl = useMemo(() => sources.creditVoucherCategories($stateParams.gymId), [
    $stateParams.gymId,
  ]);

  const getCategories = () => {
    api.get(baseUrl, { count: 1000 }).then(
      (data) => {
        setCategories(data);
      },
      () => {
        setCategories([]);
      },
    );
  };

  useEffect(() => {
    getCategories();
  }, []);

  const close = () => {
    modalInstance.close();
  };

  const cancel = () => {
    modalInstance.dismiss('cancel');
  };

  const create = () => {
    setLoading(true);
    api.post(baseUrl, { credit_voucher_category: newCategory }).then(
      () => {
        setNewCategory({ name: '' });
        setLoading(false);
        getCategories();
      },
      (response) => {
        setLoading(false);
        setNewCategory((item) => ({ ...item, errors: response.data.errors }));
      },
    );
  };

  const save = () => {
    setLoading(true);
    api.put(`${baseUrl}/${editCategory.id}`, { credit_voucher_category: editCategory }).then(
      () => {
        setEditCategory({});
        setLoading(false);
        getCategories();
      },
      (response) => {
        setLoading(false);
        setEditCategory((item) => ({ ...item, errors: response.data.errors }));
      },
    );
  };

  const remove = (category) => {
    if (window.confirm('Are you sure?')) {
      api.delete(`${baseUrl}/${category.id}`).then(
        () => {
          getCategories();
        },
        () => {
          notification.error('Request could not be completed. Please try again');
        },
      );
    }
  };

  return (
    <div>
      <div className="modal-header">
        <button
          aria-hidden="true"
          type="button"
          className="close-modal"
          onClick={cancel}
          title="Close"
        >
          <i className="icon-cross"></i>
        </button>
        <h4 className="modal-title">Manage credit categories</h4>
      </div>
      <div className="modal-body">
        <div className="form-group">
          <div className="flex">
            <div className="flex-grow">
              <Input
                autoFocus
                value={newCategory.name}
                placeholder="Enter category name"
                onChange={(name) => {
                  setNewCategory({ name: name as string });
                }}
              />
              <ErrorMessage error={newCategory.errors?.name} />
            </div>
            <div className="margin-left">
              <Button
                className="btn-block"
                onClick={() => create()}
                disabled={!newCategory.name || isLoading}
                variant="primary"
              >
                + Add
              </Button>
            </div>
          </div>
        </div>
        <div className="content-box-mini last-one">
          {categories.length ? (
            <table className="table worx-table">
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td>
                      {category.id === editCategory.id ? (
                        <div>
                          <Input
                            autoFocus
                            value={editCategory.name}
                            placeholder="Enter category name"
                            onChange={(name) =>
                              setEditCategory((item) => ({ ...item, name: name as string }))
                            }
                          />
                          <ErrorMessage error={editCategory.errors?.name} />
                        </div>
                      ) : (
                        <span className="worx-table-primary-text">{category.name}</span>
                      )}
                    </td>
                    <td>{category.credit_vouchers_count} credit voucher(s)</td>

                    <td>
                      {category.id === editCategory.id ? (
                        <div className="worx-context-menu text-right">
                          <Button size="sm" onClick={() => setEditCategory({})}>
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => save()}
                            disabled={!editCategory.name || isLoading}
                          >
                            Save
                          </Button>
                        </div>
                      ) : (
                        <div className="worx-context-menu text-right">
                          <Button
                            variant="icon--v2"
                            size="sm"
                            onClick={() => setEditCategory(category)}
                          >
                            <Icon name="edit" width={18} height={18} />
                          </Button>
                          <Button size="sm" variant="icon--v2" onClick={() => remove(category)}>
                            <Icon name="trash" width={18} height={18} />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="helper-text">You haven't added any categories yet.</div>
          )}
        </div>
      </div>
      <div className="modal-footer">
        <Button onClick={close} variant="success">
          OK
        </Button>
      </div>
    </div>
  );
};

angular.module('gym').value('ManageCreditVoucherCategories', ManageCreditVoucherCategories);
